import { Button, Icon, Input, Select, SelectOption } from '@repo/ui';
import { ArCollectionQueueParams, DaysPastDueOptions, GroupByOptions } from '../../types';
import { Dispatch, SetStateAction, useMemo } from 'react';
import { MatterTableGroupByOptions } from '../../constants';
import { GroupByOption } from '@repo/types';
import CollectionExportButton from './CollectionExportButton';

type ActionToolbarProps = {
  selectedClient: string;
  isFilterByAtty: boolean;
  isFilterByClient: boolean;
  matterGroupBy: GroupByOption;
  groupBy: string;
  onBackToClients: () => void;
  onMatterGroupByChange: Dispatch<SetStateAction<GroupByOption>>;
  // Search props
  search: string;
  onSearchChange: (value: string) => void;
  // Export props
  queryParams: ArCollectionQueueParams;
  isDataLoading: boolean;
};

/**
 * MatterActionToolbar - Renders the action toolbar for the Matters drilldown view.
 *
 * Search placeholder depends on the current navigation context:
 *
 * | User Flow                                                                | Placeholder                              |
 * |--------------------------------------------------------------------------|------------------------------------------|
 * | Group by Client → View matters                                           | "Search matter or attorney"             |
 * | Group by Attorney → View matters → No Grouping                           | "Search matter, client, or attorney"     |
 * | Group by Attorney → View matters → Group by Client                       | "Search client"                          |
 * | Group by Attorney → View matters → Group by Client → View a client       | "Search matter or attorney"             |
 */
export default function MatterActionToolbar({
  isFilterByAtty,
  isFilterByClient,
  selectedClient,
  groupBy,
  matterGroupBy,
  onBackToClients,
  onMatterGroupByChange,
  search,
  onSearchChange,
  queryParams,
  isDataLoading,
}: ActionToolbarProps) {
  const exportFilename = useMemo(() => {
    const baseFilenameParts = ['collection', 'groupBy', queryParams.filters?.groupBy];
    if (groupBy) {
      baseFilenameParts.push(groupBy);
    }
    if (matterGroupBy && matterGroupBy !== GroupByOption.NONE) {
      baseFilenameParts.push(matterGroupBy);
    }
    if (selectedClient) {
      baseFilenameParts.push(selectedClient);
    }
    return baseFilenameParts.filter(Boolean).join('-');
  }, [queryParams.filters?.groupBy, groupBy, matterGroupBy, selectedClient]);

  const exportMeta = useMemo(() => {
    const originalGroupBy = queryParams.filters?.groupBy;
    const daysPastDue = queryParams.filters?.daysPastDue;
    const groupByLabel = GroupByOptions.find(o => o.value === originalGroupBy)?.label || '';
    const matterGroupByLabel = MatterTableGroupByOptions.find(o => o.value === matterGroupBy)?.label || '';
    const daysPastDueLabel = DaysPastDueOptions.find(o => o.value === `${daysPastDue}`)?.label || '';

    const titleParts = ['Collections Queue', groupByLabel, groupBy];
    if (selectedClient) titleParts.push(selectedClient);

    const headers: string[][] = [];
    headers.push(['Group By', groupByLabel]);
    if (groupBy) headers.push([groupByLabel, groupBy]);
    if (selectedClient) headers.push(['Client', selectedClient]);
    if (matterGroupBy && matterGroupBy !== GroupByOption.NONE) {
      headers.push(['Matter Group By', matterGroupByLabel]);
    }
    headers.push(['Days Past Due', daysPastDueLabel]);

    return {
      title: titleParts.filter(Boolean).join(' - '),
      headers,
    };
  }, [queryParams.filters?.groupBy, queryParams.filters?.daysPastDue, groupBy, selectedClient, matterGroupBy]);

  let searchPlaceholder = 'Search matter, client, or attorney';
  if (isFilterByClient || selectedClient) {
    searchPlaceholder = 'Search matter or attorney';
  } else if (matterGroupBy === GroupByOption.CLIENT) {
    searchPlaceholder = 'Search client';
  }

  return (
    <div className="flex flex-col w-full gap-2">
      <div className="flex justify-start items-center gap-4">
        <Button
          variant="ghost"
          className="flex items-center gap-1 font-medium text-xtnd-dark dark:text-xtnd-light"
          onClick={onBackToClients}
        >
          <Icon name="FaAngleLeft" /> Go back
        </Button>
        <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          {`${groupBy} ${selectedClient ? `- ${selectedClient} ` : ''} - Matters`}
        </div>
      </div>
      <div className="flex justify-between gap-2 mt-2">
        {/* Local Search Input */}
        <div className="relative h-10 flex-1 duration-300 max-w-lg">
          <Icon
            name="HiSearch"
            size="sm"
            className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400"
          />
          <Input
            value={search}
            variant="outlined"
            onChange={e => onSearchChange(e.target.value)}
            placeholder={searchPlaceholder}
            className="flex w-full px-3 py-2 pl-9 border border-xtnd-light dark:border-xtnd-dark focus:ring-offset-xtnd-blue focus:ring-transparent focus:border-xtnd-blue dark:focus:border-xtnd-blue"
          />
        </div>

        <div className="flex gap-4">
          {isFilterByAtty && !selectedClient && (
            <Select<SelectOption>
              className="w-fit min-w-60"
              options={MatterTableGroupByOptions}
              value={matterGroupBy}
              disabled={isDataLoading}
              onChange={option => {
                if (typeof option === 'string') {
                  onMatterGroupByChange(option as GroupByOption);
                  return;
                }

                onMatterGroupByChange(option?.value as GroupByOption);
              }}
            />
          )}

          <CollectionExportButton
            queryParams={queryParams}
            exportFilename={exportFilename}
            isDataLoading={isDataLoading}
            exportMeta={exportMeta}
          />
        </div>
      </div>
    </div>
  );
}
